import React, { useEffect, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { Link } from 'react-router-dom'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import organisationService from 'services/organisationService'
import { hideEmail } from 'utils/user'
import { PRIVATE_PATHS } from 'utils/paths'

const OrganisationPanel = () => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    organisationService.getMine()
      .then(res => { if (!cancelled) setData(res.data?.data ?? null) })
      .catch(() => { if (!cancelled) setData(null) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  if (loading) {
    return (
      <Wrap>
        <SkeletonLine style={{ width: '90%' }} />
        <SkeletonLine style={{ width: '40%', height: 20, marginTop: 6 }} />
        <SkeletonRow>
          <SkeletonChip />
          <SkeletonChip />
        </SkeletonRow>
      </Wrap>
    )
  }

  if (!data || !data.domains?.length) {
    return (
      <Wrap>
        <Description>
          We couldn&apos;t work out your organisation. This usually means your account doesn&apos;t have a
          work email address on it yet.
        </Description>
      </Wrap>
    )
  }

  const { name, yourDomain, domains, isGrouped, superAdmins, isSuperAdmin, organisationId } = data
  const heading = name || yourDomain
  const multiple = domains.length > 1

  return (
    <Wrap>
      <Description>
        Your organisation is your company. Everyone who signs in with one of the email domains below
        is part of it and works in a shared space.
      </Description>

      <OrgName>{heading}</OrgName>

      <Label>Email domains in this organisation</Label>
      <DomainList>
        {domains.map(domain => (
          <DomainChip key={domain}>
            @{domain}
            {domain === yourDomain && <You>you</You>}
          </DomainChip>
        ))}
      </DomainList>
      {isGrouped && (
        <DomainNote>These domains are linked because they belong to the same company.</DomainNote>
      )}

      <SectionTitle>Shared automatically across your organisation</SectionTitle>
      <ItemList>
        <Item><CheckCircleRoundedIcon style={{ fontSize: 18, color: '#15803d', flex: '0 0 auto' }} /><span>Contractors, contracts and purchase orders</span></Item>
        <Item><CheckCircleRoundedIcon style={{ fontSize: 18, color: '#15803d', flex: '0 0 auto' }} /><span>Your compliance setup — custom obligations, categories and any renames</span></Item>
        <Item><CheckCircleRoundedIcon style={{ fontSize: 18, color: '#15803d', flex: '0 0 auto' }} /><span>Your organisation&apos;s shared dashboard layout</span></Item>
      </ItemList>
      <SectionHint>
        Everyone at {multiple ? 'these domains' : 'this domain'} can see and work on these — you don&apos;t
        need to share them one by one.
      </SectionHint>

      <SectionTitle>Not shared automatically</SectionTitle>
      <ItemList>
        <Item $muted><LockOutlinedIcon style={{ fontSize: 17, color: '#6e6e78', flex: '0 0 auto' }} /><span>Individual Building Passports and Unit Passports</span></Item>
        <Item $muted><LockOutlinedIcon style={{ fontSize: 17, color: '#6e6e78', flex: '0 0 auto' }} /><span>Documents and portfolios</span></Item>
      </ItemList>
      <SectionHint>
        These are only shared when someone shares them with your company, or with you directly. Being
        in the same organisation doesn&apos;t, on its own, let you see a colleague&apos;s buildings.
      </SectionHint>

      {organisationId && (
        <>
          <SectionTitle>Super Admins</SectionTitle>
          {superAdmins && superAdmins.length > 0 ? (
            <ItemList>
              {superAdmins.map(superAdmin => (
                <Item key={superAdmin.email}>
                  <span>{superAdmin.name} · {hideEmail(superAdmin.email || null)}</span>
                </Item>
              ))}
            </ItemList>
          ) : (
            <SectionHint>
              Your organisation has no Super Admin.{' '}
              <a href='mailto:info@buildingpassport.com'>Contact Building Passport support</a> to appoint one.
            </SectionHint>
          )}
          {isSuperAdmin && (
            <ManageLink to={PRIVATE_PATHS.organisation}>Manage Super Admins</ManageLink>
          )}
        </>
      )}

      <Help>
        Does your company use more than one email domain, or need one removed? Because adding a domain
        gives everyone with that email access to the shared items above, we set this up for you.{' '}
        <a href='mailto:info@buildingpassport.com'>Contact support</a> and we&apos;ll sort it out.
      </Help>
    </Wrap>
  )
}

export default OrganisationPanel

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const Description = styled.p`
  margin: 0 0 12px 0;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.grey[200]};
  line-height: 1.55;
`

const OrgName = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
`

const Label = styled.div`
  margin-top: 14px;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
`

const DomainList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`

const DomainChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 12px;
  border-radius: 8px;
  background: #f4f5f7;
  border: 1px solid #e4e6eb;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};
`

const You = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #15803d;
  background: #dcfce7;
  padding: 2px 6px;
  border-radius: 5px;
`

const DomainNote = styled.p`
  margin: 8px 0 0 0;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[200]};
`

const SectionTitle = styled.div`
  margin-top: 20px;
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
`

const ItemList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 10px;
`

const Item = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 9px;
  font-size: 14px;
  line-height: 1.4;
  color: ${({ $muted, theme }) => ($muted ? theme.colors.grey[200] : theme.colors.haiti)};

  svg { margin-top: 1px; }
`

const SectionHint = styled.p`
  margin: 8px 0 0 0;
  font-size: 12.5px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.grey[200]};
`

const ManageLink = styled(Link)`
  display: inline-block;
  margin-top: 10px;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.secondary};
`

const Help = styled.p`
  margin: 18px 0 0 0;
  padding-top: 14px;
  border-top: 1px solid #eceef1;
  font-size: 13px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.grey[200]};

  a {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 600;
  }
`

const pulse = keyframes`
  0% { opacity: 0.55; }
  50% { opacity: 1; }
  100% { opacity: 0.55; }
`

const SkeletonLine = styled.div`
  height: 13px;
  width: 100%;
  border-radius: 6px;
  background: #eceef1;
  animation: ${pulse} 1.4s ease-in-out infinite;
`

const SkeletonRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 14px;
`

const SkeletonChip = styled.div`
  height: 34px;
  width: 150px;
  border-radius: 8px;
  background: #eceef1;
  animation: ${pulse} 1.4s ease-in-out infinite;
`
